import type { SxProps } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';

import useSWR from 'swr';
import { useState } from 'react';
import { useParams, useNavigate } from 'react-router';

import Box from '@mui/material/Box';

import { fetcher } from 'src/lib/axios';
import { DashboardContent } from 'src/layouts/dashboard/content';
import { TxDataGrid } from 'src/auditoria/components/tx-data-grid';
import {
  DsNum,
  DsButton,
  GradeTabs,
  GradeCanvas,
  DsPageHeader,
  DsStatusChip,
  SX_GRADE_CANVAS,
} from 'src/auditoria/components/ds';

import {
  type ImovelDetalhe,
  type Proprietario,
  type ValorDeclarado,
  type AvaliacaoImovel,
  type CaracteristicaImovel,
  formatArea,
  formatCurrency,
  formatInscricao,
  getTipoImovelLabel,
  getStatusImovelLabel,
  getSituacaoAvaliacaoLabel,
  getFinalidadeAvaliacaoLabel,
} from './types';

// ----------------------------------------------------------------------
// Cadastro completo do imóvel. O canvas mostra o detalhe resumido na própria
// lista (coluna de 330px, ver `List.tsx`); esta rota guarda o que não cabe lá —
// proprietários, características, valores declarados e avaliações.
//
// As abas passaram para dentro do casulo da grade (§4.1): no canvas `.tabs` é o
// primeiro filho do card, nunca um bloco solto acima dele.
// ----------------------------------------------------------------------

// O `TxDataGrid` do portal declara `sx?: SxProps` (tema default do MUI) enquanto o
// kit tipa `SxProps<Theme>` do tema do app. Mesma forma, tipos nominalmente
// distintos: o estreitamento acontece uma vez aqui, sem `any` e sem `@ts-ignore`.
const { sx: SX_GRADE_KIT, ...PROPS_GRADE } = SX_GRADE_CANVAS;
const SX_GRADE = SX_GRADE_KIT as SxProps;

const TABS = [
  { value: 'dados', label: 'Dados' },
  { value: 'proprietarios', label: 'Proprietários' },
  { value: 'caracteristicas', label: 'Características' },
  { value: 'valores', label: 'Valores Declarados' },
  { value: 'avaliacoes', label: 'Avaliações' },
];

/** Listas auxiliares do imóvel são curtas; o canvas não desenha rodapé aqui. */
const TAMANHO_PAGINA = 100;

const STATUS_ATIVO = 1;
const STATUS_BLOQUEADO = 3;

function tomStatus(status: number): 'success' | 'error' | 'default' {
  if (status === STATUS_ATIVO) return 'success';
  if (status === STATUS_BLOQUEADO) return 'error';
  return 'default';
}

/** `.kv` do canvas: rótulo à esquerda, valor tabular à direita, régua tracejada. */
function LinhaKv({ rotulo, valor }: { rotulo: string; valor: React.ReactNode }) {
  const vazio = valor === null || valor === undefined || valor === '';
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: '10px',
        padding: '7px 0',
        fontSize: 12,
        borderBottom: '1px dashed',
        borderColor: 'divider',
      }}
    >
      <Box component="span" sx={{ color: 'text.secondary' }}>
        {rotulo}
      </Box>
      <DsNum sx={{ fontWeight: 700, textAlign: 'right' }}>{vazio ? '—' : valor}</DsNum>
    </Box>
  );
}

// ----------------------------------------------------------------------

function DadosTab({ id }: { id: string }) {
  const { data: d } = useSWR<ImovelDetalhe>(`/portal-api/meu-imovel/${id}`, fetcher);

  if (!d) {
    return (
      <Box sx={{ padding: '18px 20px', fontSize: 12, color: 'text.secondary' }}>
        Carregando dados cadastrais…
      </Box>
    );
  }

  return (
    <Box
      sx={{
        padding: '18px 20px',
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
        columnGap: '32px',
      }}
    >
      <LinhaKv rotulo="Inscrição" valor={formatInscricao(d.inscricao)} />
      <LinhaKv rotulo="Inscrição anterior" valor={formatInscricao(d.inscricao_anterior)} />
      <LinhaKv rotulo="Tipo" valor={getTipoImovelLabel(d.tipo_enum)} />
      <LinhaKv
        rotulo="Situação"
        valor={
          <DsStatusChip tone={tomStatus(d.status_enum)}>
            {getStatusImovelLabel(d.status_enum)}
          </DsStatusChip>
        }
      />
      <LinhaKv
        rotulo="Endereço"
        valor={[d.logradouro, d.numero_porta].filter(Boolean).join(', ')}
      />
      <LinhaKv rotulo="Complemento" valor={d.complemento} />
      <LinhaKv rotulo="Bairro" valor={d.bairro} />
      <LinhaKv rotulo="Referência" valor={d.referencia} />
      <LinhaKv rotulo="Quadra / Lote" valor={`${d.quadra || '—'} / ${d.lote || '—'}`} />
      <LinhaKv rotulo="Lado" valor={d.lado} />
      <LinhaKv rotulo="Seção" valor={d.secao || null} />
      <LinhaKv rotulo="Condomínio" valor={d.condominio} />
      <LinhaKv rotulo="Área do lote (m²)" valor={formatArea(d.area_lote)} />
      <LinhaKv rotulo="Área construída (m²)" valor={formatArea(d.area_construida_unidade)} />
      <LinhaKv rotulo="Responsável" valor={d.responsavel} />
      <LinhaKv rotulo="CPF / CNPJ" valor={d.cpf_cnpj} />
      <LinhaKv rotulo="Isento IPTU" valor={d.imovisento_iptu ? 'Sim' : 'Não'} />
      <LinhaKv rotulo="Isento TSU" valor={d.imovisento_tsu ? 'Sim' : 'Não'} />
      <LinhaKv rotulo="Desmembramento" valor={d.desmembramento ? 'Sim' : 'Não'} />
      <LinhaKv rotulo="Registro cartório" valor={d.registro_cart} />
      {d.observacoes && <LinhaKv rotulo="Observações" valor={d.observacoes} />}
    </Box>
  );
}

function ProprietariosTab({ id }: { id: string }) {
  const { data, isLoading } = useSWR<Proprietario[]>(
    `/portal-api/meu-imovel/${id}/proprietarios`,
    fetcher
  );
  const columns: GridColDef<Proprietario>[] = [
    { field: 'nome', headerName: 'Nome', flex: 1.5, minWidth: 200 },
    {
      field: 'cpf_cnpj',
      headerName: 'CPF / CNPJ',
      flex: 1,
      minWidth: 160,
      renderCell: (params) => <DsNum mono>{params.row.cpf_cnpj ?? '—'}</DsNum>,
    },
    {
      field: 'percentual',
      headerName: 'Participação',
      flex: 0.6,
      minWidth: 120,
      valueGetter: (_v, row) => `${row.percentual}%`,
    },
  ];
  return (
    <TxDataGrid<Proprietario>
      rows={data ?? []}
      columns={columns}
      loading={isLoading}
      emptyLabel="Nenhum proprietário registrado"
      initialPageSize={TAMANHO_PAGINA}
      hideFooter
      {...PROPS_GRADE}
      sx={SX_GRADE}
    />
  );
}

function CaracteristicasTab({ id }: { id: string }) {
  const { data, isLoading } = useSWR<CaracteristicaImovel[]>(
    `/portal-api/meu-imovel/${id}/caracteristicas`,
    fetcher
  );
  const columns: GridColDef<CaracteristicaImovel>[] = [
    { field: 'item', headerName: 'Item', flex: 1, minWidth: 180 },
    { field: 'valor', headerName: 'Valor', flex: 1, minWidth: 180 },
  ];
  return (
    <TxDataGrid<CaracteristicaImovel>
      rows={data ?? []}
      columns={columns}
      loading={isLoading}
      emptyLabel="Nenhuma característica registrada"
      initialPageSize={TAMANHO_PAGINA}
      hideFooter
      {...PROPS_GRADE}
      sx={SX_GRADE}
    />
  );
}

function ValoresTab({ id }: { id: string }) {
  const { data, isLoading } = useSWR<ValorDeclarado[]>(
    `/portal-api/meu-imovel/${id}/valores-declarados`,
    fetcher
  );
  const columns: GridColDef<ValorDeclarado>[] = [
    { field: 'exercicio', headerName: 'Exercício', flex: 0.6, minWidth: 120 },
    {
      field: 'valor',
      headerName: 'Valor declarado',
      flex: 1,
      minWidth: 160,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (
        <DsNum mono align="right" sx={{ fontWeight: 700 }}>
          {formatCurrency(params.row.valor)}
        </DsNum>
      ),
    },
  ];
  return (
    <TxDataGrid<ValorDeclarado>
      rows={data ?? []}
      columns={columns}
      loading={isLoading}
      emptyLabel="Nenhum valor declarado"
      initialPageSize={TAMANHO_PAGINA}
      hideFooter
      {...PROPS_GRADE}
      sx={SX_GRADE}
    />
  );
}

function AvaliacoesTab({ id }: { id: string }) {
  const { data, isLoading } = useSWR<AvaliacaoImovel[]>(
    `/portal-api/meu-imovel/${id}/avaliacoes`,
    fetcher
  );
  const columns: GridColDef<AvaliacaoImovel>[] = [
    { field: 'exercicio', headerName: 'Exercício', flex: 0.5, minWidth: 100 },
    {
      field: 'finalidade',
      headerName: 'Finalidade',
      flex: 0.6,
      minWidth: 110,
      valueGetter: (_v, row) => getFinalidadeAvaliacaoLabel(row.finalidade),
    },
    {
      field: 'vvt',
      headerName: 'VVT',
      flex: 0.7,
      minWidth: 130,
      valueGetter: (_v, row) => formatCurrency(row.vvt),
    },
    {
      field: 'vve',
      headerName: 'VVE',
      flex: 0.7,
      minWidth: 130,
      valueGetter: (_v, row) => formatCurrency(row.vve),
    },
    {
      field: 'vvi',
      headerName: 'Valor venal',
      flex: 0.8,
      minWidth: 140,
      valueGetter: (_v, row) => formatCurrency(row.vvi),
    },
    {
      field: 'aliquota',
      headerName: 'Alíquota',
      flex: 0.5,
      minWidth: 100,
      valueGetter: (_v, row) => `${row.aliquota}%`,
    },
    {
      field: 'valor_iptu',
      headerName: 'IPTU',
      flex: 0.8,
      minWidth: 130,
      valueGetter: (_v, row) => formatCurrency(row.valor_iptu),
    },
    {
      field: 'situacao',
      headerName: 'Situação',
      flex: 0.8,
      minWidth: 150,
      valueGetter: (_v, row) => getSituacaoAvaliacaoLabel(row.situacao),
    },
    {
      field: 'data_avaliacao',
      headerName: 'Data',
      flex: 0.6,
      minWidth: 120,
      // data_avaliacao vem como 'yyyy-MM-dd' (formatarDataIso no backend) → DD/MM/YYYY.
      valueGetter: (_v, row) =>
        row.data_avaliacao ? row.data_avaliacao.split('-').reverse().join('/') : '—',
    },
  ];
  return (
    <TxDataGrid<AvaliacaoImovel>
      rows={data ?? []}
      columns={columns}
      loading={isLoading}
      emptyLabel="Nenhuma avaliação registrada"
      initialPageSize={TAMANHO_PAGINA}
      hideFooter
      {...PROPS_GRADE}
      sx={SX_GRADE}
    />
  );
}

// ----------------------------------------------------------------------

export default function MeuImovelDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState('dados');

  return (
    <DashboardContent>
      <DsPageHeader
        weight={800}
        title="Detalhe do Imóvel"
        subtitle="Cadastro completo · proprietários, características, valores declarados e avaliações."
        action={
          <DsButton tone="neutral" size="tall" onClick={() => navigate('/meu-imovel')}>
            Voltar aos imóveis
          </DsButton>
        }
      />

      <GradeCanvas tabs={<GradeTabs value={tab} onChange={setTab} items={TABS} />}>
        {tab === 'dados' && <DadosTab id={id} />}
        {tab === 'proprietarios' && <ProprietariosTab id={id} />}
        {tab === 'caracteristicas' && <CaracteristicasTab id={id} />}
        {tab === 'valores' && <ValoresTab id={id} />}
        {tab === 'avaliacoes' && <AvaliacoesTab id={id} />}
      </GradeCanvas>
    </DashboardContent>
  );
}
